import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  ChevronRight,
  TreePine,
  Church,
  Users,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const tiposEntidad = [
  { value: "conferencia_episcopal", label: "Conferencia Episcopal", nivel: 2 },
  { value: "provincia_eclesiastica", label: "Provincia Eclesiástica", nivel: 3 },
  { value: "arquidiocesis", label: "Arquidiócesis", nivel: 4 },
  { value: "diocesis", label: "Diócesis", nivel: 4 },
  { value: "prelatura_territorial", label: "Prelatura Territorial", nivel: 4 },
  { value: "vicariato_apostolico", label: "Vicariato Apostólico", nivel: 4 },
  { value: "vicaria_general", label: "Vicaría General", nivel: 5 },
  { value: "vicaria_episcopal", label: "Vicaría Episcopal", nivel: 5 },
  { value: "decanato", label: "Decanato/Vicaría Foránea", nivel: 5 },
  { value: "parroquia", label: "Parroquia", nivel: 6 },
  { value: "cuasi_parroquia", label: "Cuasi-parroquia", nivel: 7 },
  { value: "rectoria", label: "Rectoría", nivel: 7 },
  { value: "capilla", label: "Capilla", nivel: 7 },
  { value: "santuario", label: "Santuario", nivel: 7 },
  { value: "centro_pastoral", label: "Centro Pastoral", nivel: 7 },
];

const getIconForType = (tipo) => {
  if (!tipo) return <Building2 className="w-5 h-5" />;
  if (tipo.includes("parroquia") || tipo.includes("capilla") || tipo.includes("santuario"))
    return <Church className="w-5 h-5" />;
  if (tipo.includes("diocesis") || tipo.includes("arquidiocesis"))
    return <Building2 className="w-5 h-5" />;
  if (tipo.includes("conferencia")) return <Users className="w-5 h-5" />;
  if (tipo.includes("decanato") || tipo.includes("vicaria")) return <MapPin className="w-5 h-5" />;
  return <TreePine className="w-5 h-5" />;
};

const getTipoLabel = (tipo) => {
  const found = tiposEntidad.find((t) => t.value === tipo);
  return found ? found.label : tipo?.replace(/_/g, " ");
};

export default function EntidadesPage() {
  const [entidades, setEntidades] = useState([]);
  const [arbol, setArbol] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list"); // list or tree
  const [filterTipo, setFilterTipo] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingEntidad, setEditingEntidad] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    tipo: "parroquia",
    codigo: "",
    parent_id: "",
    pais: "",
    region: "",
    direccion: "",
    telefono: "",
    email: "",
    titular: "",
    notas: "",
    activo: true,
  });

  useEffect(() => {
    loadEntidades();
  }, []);

  const loadEntidades = async () => {
    try {
      setLoading(true);
      const [listRes, arbolRes] = await Promise.all([
        api.get("/entidades"),
        api.get("/entidades/arbol"),
      ]);
      setEntidades(listRes.data);
      setArbol(arbolRes.data);
    } catch (error) {
      toast.error("Error al cargar las entidades");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (entidad = null) => {
    if (entidad) {
      setEditingEntidad(entidad);
      setFormData({
        nombre: entidad.nombre || "",
        tipo: entidad.tipo || "parroquia",
        codigo: entidad.codigo || "",
        parent_id: entidad.parent_id || "",
        pais: entidad.pais || "",
        region: entidad.region || "",
        direccion: entidad.direccion || "",
        telefono: entidad.telefono || "",
        email: entidad.email || "",
        titular: entidad.titular || "",
        notas: entidad.notas || "",
        activo: entidad.activo !== false,
      });
    } else {
      setEditingEntidad(null);
      setFormData({
        nombre: "",
        tipo: "parroquia",
        codigo: "",
        parent_id: "",
        pais: "",
        region: "",
        direccion: "",
        telefono: "",
        email: "",
        titular: "",
        notas: "",
        activo: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntidad(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (!data.parent_id) delete data.parent_id;

    try {
      if (editingEntidad) {
        await api.put(`/entidades/${editingEntidad.id}`, data);
        toast.success("Entidad actualizada correctamente");
      } else {
        await api.post("/entidades", data);
        toast.success("Entidad creada correctamente");
      }
      handleCloseDialog();
      loadEntidades();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la entidad");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/entidades/${deleteId}`);
      toast.success("Entidad eliminada correctamente");
      loadEntidades();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar la entidad");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredEntidades = entidades.filter((e) => {
    const matchesTipo = filterTipo === "all" || e.tipo === filterTipo;
    const matchesSearch =
      !search ||
      e.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      e.codigo?.toLowerCase().includes(search.toLowerCase());
    return matchesTipo && matchesSearch;
  });

  const TreeNode = ({ node, level = 0 }) => {
    const [isOpen, setIsOpen] = useState(level < 2);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div>
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-md hover:bg-stone-100 cursor-pointer ${
            level === 0 ? "bg-rose-50" : ""
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
          onClick={() => hasChildren && setIsOpen(!isOpen)}
        >
          {hasChildren ? (
            <ChevronRight
              className={`w-4 h-4 text-stone-400 transition-transform ${
                isOpen ? "rotate-90" : ""
              }`}
            />
          ) : (
            <span className="w-4" />
          )}
          <span className="text-stone-400">{getIconForType(node.tipo)}</span>
          <span className="flex-1 text-sm text-stone-700">{node.nombre}</span>
          <Badge variant="outline" className="text-xs">
            {getTipoLabel(node.tipo)}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(node);
            }}
          >
            <Edit className="w-3 h-3" />
          </Button>
        </div>
        {hasChildren && isOpen && (
          <div>
            {node.children.map((child) => (
              <TreeNode key={child.id} node={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="entidades-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Building2 className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
            Entidades Jurisdiccionales
          </h1>
          <p className="text-stone-600 mt-1">Gestión de la estructura jerárquica de la Iglesia</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-rose-950 hover:bg-rose-900 text-white"
          data-testid="nueva-entidad-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Entidad
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Buscar por nombre o código..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10 bg-white border-stone-200"
              />
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[200px] h-10">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tiposEntidad.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
                className={viewMode === "list" ? "bg-rose-950" : ""}
              >
                Lista
              </Button>
              <Button
                variant={viewMode === "tree" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("tree")}
                className={viewMode === "tree" ? "bg-rose-950" : ""}
              >
                Árbol
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-stone-400">Cargando...</div>
        </div>
      ) : viewMode === "tree" ? (
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">Vista de Árbol</CardTitle>
          </CardHeader>
          <CardContent>
            {arbol.length === 0 ? (
              <div className="py-8 text-center text-stone-500">
                <TreePine className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay entidades registradas</p>
              </div>
            ) : (
              <div className="space-y-1">
                {arbol.map((node) => (
                  <TreeNode key={node.id} node={node} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEntidades.length === 0 ? (
            <Card className="col-span-full border-stone-200 bg-white">
              <CardContent className="py-8 text-center text-stone-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No se encontraron entidades</p>
              </CardContent>
            </Card>
          ) : (
            filteredEntidades.map((entidad) => (
              <Card
                key={entidad.id}
                className={`border-stone-200 bg-white card-hover ${
                  !entidad.activo ? "opacity-60" : ""
                }`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-stone-100 text-stone-600">
                      {getIconForType(entidad.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-stone-800 truncate">{entidad.nombre}</h3>
                      <p className="text-sm text-stone-500">{getTipoLabel(entidad.tipo)}</p>
                      {entidad.titular && (
                        <p className="text-xs text-stone-400 mt-1">Titular: {entidad.titular}</p>
                      )}
                      {entidad.pais && (
                        <p className="text-xs text-stone-400">
                          {entidad.region ? `${entidad.region}, ` : ""}
                          {entidad.pais}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
                    <Badge
                      variant="outline"
                      className={`${
                        entidad.activo
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-stone-100 text-stone-500"
                      }`}
                    >
                      {entidad.activo ? "Activa" : "Inactiva"}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenDialog(entidad)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteId(entidad.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingEntidad ? "Editar Entidad" : "Nueva Entidad"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {tiposEntidad.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo">Código</Label>
                <Input
                  id="codigo"
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleChange}
                  className="h-10"
                  placeholder="Ej: PAR-001"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="parent_id">Entidad Superior</Label>
                <Select
                  value={formData.parent_id || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, parent_id: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-[200px]">
                    <SelectItem value="none">Sin entidad superior</SelectItem>
                    {entidades
                      .filter((e) => e.id !== editingEntidad?.id)
                      .map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.nombre} ({getTipoLabel(e.tipo)})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pais">País</Label>
                <Input
                  id="pais"
                  name="pais"
                  value={formData.pais}
                  onChange={handleChange}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Región/Estado</Label>
                <Input
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="h-10"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-10"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="titular">Titular (Obispo/Párroco)</Label>
                <Input
                  id="titular"
                  name="titular"
                  value={formData.titular}
                  onChange={handleChange}
                  className="h-10"
                  placeholder="Nombre del titular actual"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="notas">Notas</Label>
                <Textarea
                  id="notas"
                  name="notas"
                  value={formData.notas}
                  onChange={handleChange}
                  rows={2}
                  className="resize-none"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-rose-950 hover:bg-rose-900 text-white">
                {editingEntidad ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta entidad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La entidad será eliminada permanentemente. Asegúrese
              de que no tenga entidades dependientes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
